import { action } from '@ember/object';
import { next } from '@ember/runloop';
import { service } from '@ember/service';
import { cached, tracked } from '@glimmer/tracking';

import type { APIData, PopupData, RequestError, Service } from 'webapp-types';
import { PopupContextSwitchAction as ContextSwitchAction } from 'webapp/enums/enums';
import { TrackingEvent } from 'webapp/enums/tracking';
import { isAlive, uniq } from 'webapp/utils/common';

import CommonUiIconComponent from 'webapp/components/common/ui-icon';
import FormRadiobuttonComponent from 'webapp/components/form/radiobutton';
import PopupInnerModalComponent from 'webapp/components/popup/inner/modal';
import ResolveDataItemChoiceComponent from 'webapp/components/resolve-data-item-choice';
import SubmitButtonComponent from 'webapp/components/submit-button';

import type Board from 'webapp/models-custom/board';
import type { SourceData } from 'webapp/models-custom/resolve-sources-data';

export default class BoardPopupDuplicateComponent extends PopupInnerModalComponent<PopupData.Board.Duplicate> {
	@service('store') declare storeService: Service.Store;
	@service('navigation') declare navigationService: Service.Navigation;
	@service('analytics') declare analyticsService: Service.Analytics;
	@service('request') declare requestService: Service.Request;
	@service('infolayer') declare infolayerService: Service.Infolayer;
	@service('model-manager/boards') declare mmBoardsService: Service.ModelManager.Boards;
	@service('model-manager/source-types')
	declare mmSourceTypesService: Service.ModelManager.SourceTypes;

	@tracked chosenDuplicateOption = 0;
	@tracked chosenDuplicateSources: Record<string, number> = {}; // source type id -> source id

	get model() {
		return this.popupData.model;
	}

	@cached
	get connectableSourceTypes() {
		const sourceTypes = this.model.sourcesRecords
			.filter((s) => !!s.type)
			.map((item) => item.type);
		return uniq(sourceTypes);
	}

	@cached
	get connectableSourceData(): SourceData[] {
		return this.connectableSourceTypes.map((sourceTypeId) => ({
			sourceType: this.mmSourceTypesService.findModelById(sourceTypeId)!,
		}));
	}

	get allowSelectSources() {
		return this.connectableSourceTypes.length > 0;
	}

	get disableChoosing() {
		return !this.allowSelectSources;
	}

	@cached
	get dataSourceSingularOrPlural() {
		const isMultiple = this.connectableSourceTypes.length > 1;
		return 'data source' + (isMultiple ? 's' : '');
	}

	get sameSourceCopyText() {
		return `Copy dashboard with same ${this.dataSourceSingularOrPlural}`;
	}

	get sameSourceCopy2Text() {
		return this.model.sourcesRecords.map((source) => source.title).join(', ');
	}

	get differentSourceCopyText() {
		return `Replace with different ${this.dataSourceSingularOrPlural}`;
	}

	validateForm() {
		let isValid = false;

		// check if radio "replace with different source" is selected
		if (this.chosenDuplicateOption !== 0) {
			// check if different source is selected from dropdown
			if (
				Object.keys(this.chosenDuplicateSources).length ===
				this.connectableSourceTypes.length
			) {
				isValid = true;
			}
		} else {
			isValid = true;
		}

		return isValid;
	}

	getData() {
		let params: { dataSourceTypeMap: Record<string, number> } | null = null;
		if (this.chosenDuplicateOption !== 0 && Object.keys(this.chosenDuplicateSources).length) {
			params = {
				dataSourceTypeMap: this.chosenDuplicateSources,
			};
		}
		return params;
	}

	@action
	onDuplicateOptionChange(chosenDuplicateOption: AnySimpleValue) {
		this.chosenDuplicateOption = chosenDuplicateOption as number;
	}

	@action
	duplicateDatacard() {
		if (!this.submitInProgress && this.validateForm()) {
			const layer = this.infolayerService.loading('Copying dashboard...');
			this.submitInProgress = true;

			const params = this.getData();

			this.requestService
				.api<APIData.Board.RecordResponse>('POST', `/boards/${this.model.id}/clone`, params)
				.then(
					(reply) => {
						this.storeService.pushPayload(reply);
						layer.ok(`Dashboard <strong>${this.model.name}</strong> copied!`);

						next(() => {
							const board = reply.board
								? this.mmBoardsService.findModel(String(reply.board.id))
								: null;
							this.mmBoardsService.triggerDataboardCreated(
								{ from: this.popupData.trackOrigin },
								board!,
							);
							this.analyticsService.event(TrackingEvent.DATABOARD_DUPLICATE, {
								origin: this.popupData.trackOrigin,
							});

							this.submitInProgress = false;
							if (board && this.popupData.askRedirect) {
								this.confirmRedirect(board);
							} else {
								if (board && this.popupData.onDuplicate) {
									this.popupData.onDuplicate(board);
								}
								this.close();
							}
						});
					},
					(errorData: RequestError) => {
						layer.close();
						this.submitInProgress = false;
						throw errorData;
					},
				)
				.catch(this.requestService.showAllValidationErrors.bind(this.requestService));
		}
	}

	@action
	confirmRedirect(board: Board) {
		this.navigationService.createContextSwitchModal().open({
			actionType: ContextSwitchAction.DUPLICATE,
			sourceItem: this.model,
			targetItem: board,
			onSwitch: () => {
				this.navigationService.replaceRoute(`/layout-designer/${board.id}`);
			},
		});
	}

	@action
	updateSourceId(sourceData: SourceData, sourceId: string) {
		if (isAlive(this)) {
			const type = sourceData.sourceType.id!;
			if (sourceId) {
				this.chosenDuplicateOption = 1;
				this.chosenDuplicateSources[type] = +sourceId;
			} else {
				delete this.chosenDuplicateSources[type];
			}
			this.chosenDuplicateSources = this.chosenDuplicateSources;
		}
	}
	<template>
		<div class="title">
			<CommonUiIconComponent @name="Copy" @size="lg" />
			How do you want to duplicate dashboard "{{this.model.name}}"?
		</div>
		<div class="content {{if this.allowSelectSources 'choose-enabled'}} ">
			<div class="choose-default">
				<p>
					Would you like to change the
					{{this.dataSourceSingularOrPlural}}
					{{#if this.allowSelectSources}}
						({{this.connectableSourceTypes.length}})
					{{/if}}
					for the duplicated dashboard?
				</p>
				<div class="radiobutton-default">
					<FormRadiobuttonComponent
						@name="duplicateOption"
						@selected={{this.chosenDuplicateOption}}
						@value={{0}}
						@label={{this.sameSourceCopyText}}
						@onChange={{this.onDuplicateOptionChange}}
					/>
					{{#if this.sameSourceCopy2Text}}
						<span class="lower-label">{{this.sameSourceCopy2Text}}</span>
					{{/if}}
				</div>
			</div>
			<div class="choose-source">
				<div class="radiobutton-default">
					<FormRadiobuttonComponent
						@name="duplicateOption"
						@selected={{this.chosenDuplicateOption}}
						@value={{1}}
						@label={{this.differentSourceCopyText}}
						@isDisabled={{this.disableChoosing}}
						@onChange={{this.onDuplicateOptionChange}}
					/>
				</div>
				{{#if this.allowSelectSources}}
					{{#each this.connectableSourceData as |source|}}
						<ResolveDataItemChoiceComponent
							@sourceData={{source}}
							@onChoose={{this.updateSourceId}}
							@skipLabel={{true}}
							@isDisabled={{this.submitInProgress}}
							@showAvatar={{true}}
						/>
					{{/each}}
				{{else}}
					<span class="connect-infos">
						You cannot replace the
						{{this.dataSourceSingularOrPlural}}.
					</span>
				{{/if}}
			</div>
			<div class="controls2">
				<div class="controls2__right">
					<SubmitButtonComponent
						@text="Cancel"
						@theme="grey-empty"
						@noDelay={{true}}
						@onTrigger={{this.close}}
					/>
					<SubmitButtonComponent
						@text="Duplicate"
						@extraClass="save"
						@onTrigger={{this.duplicateDatacard}}
						@submitInProgress={{this.submitInProgress}}
					/>
				</div>
			</div>

		</div>
	</template>
}
